/**
 * Text rasterisation cache (W2-01a preview-gl; ARCHITECTURE.md §7.3, rendering.md R§3.6, R§7.1).
 *
 * Text lines and HUD texts are rasterised white on transparent into canvases (OffscreenCanvas 2D where available)
 * at device scale, uploaded straight (not premultiplied) and tinted by the vertex colour like Phira's glyph quads.
 *
 *  - Key: (role, PxScale bucket, string). Buckets are quarter-octave steps rounded UP, so text is never upscaled by
 *    more than the step; a raster wider than the GPU's texture limit is made at a smaller PxScale instead.
 *  - LRU over 64 MB of raster pixels; evicted canvases are reported (`onEvict`) so their GPU textures go too.
 *  - At most `maxNewPerFrame` new rasters per frame. Over budget, a raster of the same string at another size is
 *    used when one exists, else the string is skipped for this frame and `deferred` is set (the renderer asks for
 *    another frame).
 *  - Fonts: a string is rasterised only once the faces for its code points are loaded (`PreviewFonts.ensure`);
 *    entries made under an older font generation are re-rasterised (within the budget, the old raster is drawn
 *    meanwhile) - this is how a CJK subset that arrives later replaces the fallback glyphs.
 *
 * Layout (PxScale units, y down): line i has its baseline at ascent + i * (1 + lineGap); the block runs from the
 * ascent line of the first line to the baseline of the last line (descenders excluded, R§3.6) and is as wide as the
 * widest line's advance; lines are left-aligned.
 */

import {
  context2d,
  createTextCanvas,
  fontCss,
  splitLines,
  type FontRole,
  type PreviewFonts,
  type TextCanvas,
} from './fonts';

/** A rasterised string. */
export interface TextRaster {
  readonly key: string;
  readonly role: FontRole;
  readonly text: string;
  readonly canvas: TextCanvas;
  /** Canvas size in px. */
  readonly width: number;
  readonly height: number;
  /** PxScale of the raster in px. */
  readonly pxScale: number;
  /** Padding in px around the layout box (glyph overhang). */
  readonly padX: number;
  readonly padY: number;
  /** Block width / height in PxScale units (height: first ascent line to last baseline). */
  readonly blockW: number;
  readonly blockH: number;
  /** Ascent in PxScale units (height of a single-line box). */
  readonly ascent: number;
  readonly lines: number;
  readonly generation: number;
  readonly bytes: number;
}

export interface TextCacheOptions {
  /** Pixel budget of the cache in bytes (RGBA). Default 64 MB. */
  readonly maxBytes?: number;
  /** New rasters per frame. Default 16. */
  readonly maxNewPerFrame?: number;
  /** Largest canvas side (the GPU texture limit). Default 4096. */
  readonly maxSide?: number;
}

export const TEXT_CACHE_MAX_BYTES = 64 * 1024 * 1024;
export const TEXT_MAX_NEW_PER_FRAME = 16;
/** Smallest and largest raster PxScale in px. */
export const MIN_RASTER_PX = 6;
export const MAX_RASTER_PX = 512;

/** Quarter-octave bucket at or above `px` (clamped to the raster range). */
export function pxBucket(px: number): number {
  if (!(px > MIN_RASTER_PX)) return MIN_RASTER_PX;
  if (px >= MAX_RASTER_PX) return MAX_RASTER_PX;
  const b = Math.pow(2, Math.ceil(Math.log2(px) * 4 - 1e-9) / 4);
  return Math.min(MAX_RASTER_PX, Math.max(MIN_RASTER_PX, b));
}

function keyOf(role: FontRole, bucket: number, text: string): string {
  return `${role}\u0001${String(bucket)}\u0001${text}`;
}

function textKey(role: FontRole, text: string): string {
  return `${role}\u0001${text}`;
}

export class TextCache {
  private readonly fonts: PreviewFonts;
  private readonly maxBytes: number;
  private readonly maxNew: number;
  private maxSide: number;
  /** LRU order: least recently used first. */
  private readonly entries = new Map<string, TextRaster>();
  /** Raster keys per (role, string), for the over-budget fallback. */
  private readonly byText = new Map<string, Set<string>>();
  private bytesTotal = 0;
  private madeThisFrame = 0;
  private deferredFlag = false;
  private readonly evictListeners = new Set<(canvas: TextCanvas) => void>();

  constructor(fonts: PreviewFonts, opts: TextCacheOptions = {}) {
    this.fonts = fonts;
    this.maxBytes = opts.maxBytes ?? TEXT_CACHE_MAX_BYTES;
    this.maxNew = opts.maxNewPerFrame ?? TEXT_MAX_NEW_PER_FRAME;
    this.maxSide = opts.maxSide ?? 4096;
  }

  /** Start of a frame: resets the per-frame raster budget. */
  beginFrame(): void {
    this.madeThisFrame = 0;
    this.deferredFlag = false;
  }

  /** True when the raster budget skipped a string or kept a stale one this frame (draw another frame). */
  get deferred(): boolean {
    return this.deferredFlag;
  }

  setMaxSide(side: number): void {
    if (side > 0) this.maxSide = side;
  }

  get bytes(): number {
    return this.bytesTotal;
  }

  get size(): number {
    return this.entries.size;
  }

  /** Called with every canvas that leaves the cache (release its GPU texture). */
  onEvict(cb: (canvas: TextCanvas) => void): () => void {
    this.evictListeners.add(cb);
    return () => {
      this.evictListeners.delete(cb);
    };
  }

  /**
   * Raster of `text` for drawing at `px` device pixels of PxScale (null: nothing to draw yet, or an empty string).
   * `text` may contain line breaks (text lines); HUD texts are single lines.
   */
  get(text: string, role: FontRole, px: number): TextRaster | null {
    if (text === '' || !(px > 0)) return null;
    const bucket = pxBucket(px);
    const key = keyOf(role, bucket, text);
    const hit = this.entries.get(key);
    const gen = this.fonts.generation;
    if (hit !== undefined && hit.generation === gen) return this.touch(hit);
    // Missing or made under older fonts: (re)rasterise when the faces are there and the budget allows.
    const ready = this.fonts.ensure(role, text);
    if (ready && this.madeThisFrame < this.maxNew) {
      const made = this.rasterise(text, role, bucket, key, gen);
      if (made !== null) {
        this.madeThisFrame++;
        if (hit !== undefined) this.remove(hit);
        this.insert(made);
        return made;
      }
    }
    // Over budget, another frame makes progress (deferred); waiting for fonts, their arrival asks for the redraw.
    if (ready) this.deferredFlag = true;
    if (hit !== undefined) return this.touch(hit);
    // Any size of the same string (stretched) rather than nothing.
    const others = this.byText.get(textKey(role, text));
    if (others !== undefined) {
      for (const k of others) {
        const e = this.entries.get(k);
        if (e !== undefined) return this.touch(e);
      }
    }
    return null;
  }

  private touch(e: TextRaster): TextRaster {
    this.entries.delete(e.key);
    this.entries.set(e.key, e);
    return e;
  }

  private insert(e: TextRaster): void {
    this.entries.set(e.key, e);
    this.bytesTotal += e.bytes;
    const t = textKey(e.role, e.text);
    let set = this.byText.get(t);
    if (set === undefined) {
      set = new Set();
      this.byText.set(t, set);
    }
    set.add(e.key);
    this.evictOverBudget(e.key);
  }

  private evictOverBudget(keep: string): void {
    if (this.bytesTotal <= this.maxBytes) return;
    for (const e of [...this.entries.values()]) {
      if (this.bytesTotal <= this.maxBytes) break;
      if (e.key === keep) continue;
      this.remove(e);
    }
  }

  private remove(e: TextRaster): void {
    if (this.entries.get(e.key) !== e) return;
    this.entries.delete(e.key);
    this.bytesTotal -= e.bytes;
    const t = textKey(e.role, e.text);
    const set = this.byText.get(t);
    if (set !== undefined && set.delete(e.key) && set.size === 0) this.byText.delete(t);
    for (const cb of [...this.evictListeners]) cb(e.canvas);
  }

  /** Rasterises a string (null when no 2D context exists). */
  private rasterise(text: string, role: FontRole, bucket: number, key: string, gen: number): TextRaster | null {
    const fm = this.fonts.metrics(role);
    const lines = splitLines(text);
    let blockW = 0;
    for (const l of lines) blockW = Math.max(blockW, this.fonts.advance(role, l));
    const lineAdv = fm.ascent + fm.descent + fm.lineGap;
    const blockH = (lines.length - 1) * lineAdv + fm.ascent;
    // Shrink the raster when it would exceed the texture limit.
    const padUnits = 0.3;
    const wUnits = blockW + 2 * padUnits + 0.2;
    const hUnits = blockH + fm.descent + 2 * padUnits;
    const limit = Math.max(64, this.maxSide - 4);
    const px = Math.max(1, Math.min(bucket, limit / Math.max(wUnits, 1e-6), limit / Math.max(hUnits, 1e-6)));
    const padX = Math.ceil(padUnits * px) + 1;
    const padY = Math.ceil(padUnits * px) + 1;
    const width = Math.max(1, Math.ceil((blockW + 0.2) * px) + 2 * padX);
    const height = Math.max(1, Math.ceil((blockH + fm.descent) * px) + 2 * padY);
    const canvas = createTextCanvas(width, height);
    const ctx = context2d(canvas);
    if (ctx === null) return null;
    ctx.clearRect(0, 0, width, height);
    ctx.font = fontCss(role, px * fm.cssPerPx);
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    ctx.fillStyle = '#ffffff';
    lines.forEach((l, i) => {
      if (l !== '') ctx.fillText(l, padX, padY + (fm.ascent + i * lineAdv) * px);
    });
    return {
      key,
      role,
      text,
      canvas,
      width,
      height,
      pxScale: px,
      padX,
      padY,
      blockW,
      blockH,
      ascent: fm.ascent,
      lines: lines.length,
      generation: gen,
      bytes: width * height * 4,
    };
  }

  /** Drops everything (dispose, or the GPU context was lost and rasters are re-uploaded anyway). */
  clear(): void {
    for (const e of [...this.entries.values()]) this.remove(e);
  }
}
